import { useEffect, useMemo, useRef, useState } from "react";
import { CaretDown, MagnifyingGlass } from "@phosphor-icons/react";

const selectionLabel = (operators, selectedKeys) => {
  if (selectedKeys === null || selectedKeys.length === operators.length) {
    return "All operators";
  }
  if (!selectedKeys.length) return "Select operators";

  const selectedNames = selectedKeys
    .map((key) => operators.find((operator) => operator.key === key)?.name)
    .filter(Boolean);
  if (selectedNames.length === 1) return selectedNames[0];
  if (selectedNames.length <= 3 && selectedNames.length === selectedKeys.length) {
    return `${selectedNames[0]} + ${selectedNames.length - 1} more`;
  }
  return `${selectedKeys.length} ${selectedKeys.length === 1 ? "operator" : "operators"} selected`;
};

export default function OperatorMultiSelect({ operators, selectedKeys, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const searchRef = useRef(null);

  const availableKeys = useMemo(
    () => operators.map((operator) => operator.key).filter(Boolean),
    [operators],
  );
  const effectiveSelection = selectedKeys === null ? availableKeys : selectedKeys;
  const summary = selectionLabel(operators, selectedKeys);
  const filteredOperators = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return operators;
    return operators.filter((operator) => operator.name.toLowerCase().includes(query));
  }, [operators, search]);

  useEffect(() => {
    if (!open) return undefined;
    searchRef.current?.focus();

    const closeFromOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", closeFromOutside);
    return () => document.removeEventListener("mousedown", closeFromOutside);
  }, [open]);

  const close = () => {
    setOpen(false);
    setSearch("");
    triggerRef.current?.focus();
  };

  const toggleOperator = (key) => {
    const next = effectiveSelection.includes(key)
      ? effectiveSelection.filter((item) => item !== key)
      : [...effectiveSelection, key];
    if (next.length === availableKeys.length) {
      onChange(null);
      return;
    }
    onChange(Array.from(new Set(next)).filter((item) => availableKeys.includes(item)));
  };

  return (
    <div
      ref={rootRef}
      className="relative w-full max-w-2xl"
      onKeyDown={(event) => {
        if (event.key === "Escape" && open) {
          event.preventDefault();
          close();
        }
      }}
    >
      <label id="budget-operator-label" className="overline text-white/50 block mb-2">
        Operators
      </label>
      <button
        ref={triggerRef}
        type="button"
        aria-labelledby="budget-operator-label budget-operator-summary"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls="budget-operator-popover"
        data-testid="operator-selector-trigger"
        onClick={() => {
          setOpen((current) => !current);
          if (open) setSearch("");
        }}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown") {
            event.preventDefault();
            setOpen(true);
          }
        }}
        className="flex min-h-11 w-full items-center justify-between gap-3 rounded-md border border-white/20 bg-[var(--cc-navy)] px-3 py-2 text-left text-sm text-white transition-colors hover:border-white/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--cc-gold)]"
      >
        <span id="budget-operator-summary" className="min-w-0 truncate" data-testid="operator-selection-summary">
          {summary}
        </span>
        <CaretDown
          size={16}
          weight="bold"
          aria-hidden="true"
          className={`shrink-0 text-[var(--cc-gold)] transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          id="budget-operator-popover"
          role="dialog"
          aria-label="Choose operators"
          data-testid="operator-selector-popover"
          className="absolute left-0 right-0 z-40 mt-2 overflow-hidden rounded-md border border-white/15 bg-[var(--cc-navy)] shadow-2xl shadow-black/40"
        >
          <div className="border-b border-white/10 p-3">
            <div className="relative">
              <MagnifyingGlass
                size={16}
                aria-hidden="true"
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/45"
              />
              <input
                ref={searchRef}
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                aria-label="Search operators"
                placeholder="Search operators..."
                data-testid="operator-search"
                className="h-10 w-full rounded-md border border-white/15 bg-white/[0.04] pl-9 pr-3 text-sm text-white outline-none placeholder:text-white/35 focus:border-[var(--cc-gold)] focus:ring-1 focus:ring-[var(--cc-gold)]"
              />
            </div>
            <div className="mt-3 flex items-center gap-4">
              <button
                type="button"
                onClick={() => onChange(null)}
                data-testid="operator-select-all"
                className="text-xs font-medium text-[var(--cc-gold)] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--cc-gold)]"
              >
                Select all
              </button>
              <button
                type="button"
                onClick={() => onChange([])}
                data-testid="operator-clear-all"
                className="text-xs font-medium text-white/65 hover:text-white hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--cc-gold)]"
              >
                Clear all
              </button>
            </div>
          </div>

          <div
            role="group"
            aria-label="Operator options"
            data-testid="operator-checkboxes"
            className="max-h-64 overflow-y-auto overscroll-contain p-2"
          >
            {filteredOperators.map((operator) => {
              const checked = effectiveSelection.includes(operator.key);
              const noLive = operator.temporarily_no_live_competitions || Number(operator.count) <= 0;
              return (
                <label
                  key={operator.key}
                  className={`grid min-h-11 cursor-pointer grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 rounded px-2.5 py-2 text-sm transition-colors hover:bg-white/[0.06] ${
                    checked ? "text-white" : "text-white/70"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleOperator(operator.key)}
                    data-testid={`operator-filter-${operator.key}`}
                    className="h-4 w-4 accent-[var(--cc-gold)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--cc-gold)]"
                  />
                  <span className="truncate">{operator.name}</span>
                  <span className="min-w-12 text-right font-mono-num text-xs text-white/45">
                    {noLive ? "No live" : operator.count}
                  </span>
                </label>
              );
            })}
            {!filteredOperators.length && (
              <p className="px-3 py-5 text-center text-sm text-white/45">No operators match that search.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
